<template>
  <svg
    class="version-history-graph"
    :width="svgWidth"
    :height="svgHeight"
    :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
    role="img"
    aria-label="版本历史分支图"
  >
    <line
      v-for="line in verticalConnectionLines"
      :key="line.key"
      class="version-history-graph__vertical-connection"
      :x1="line.x"
      :x2="line.x"
      :y1="line.y1"
      :y2="line.y2"
      :stroke="line.color"
      stroke-width="2"
      stroke-opacity="1"
      stroke-linecap="round"
      stroke-linejoin="round"
      shape-rendering="geometricPrecision"
    />
    <path
      v-for="path in branchPaths"
      :key="path.key"
      class="version-history-graph__branch"
      :d="path.d"
      :stroke="path.color"
      stroke-width="2"
      stroke-opacity="1"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <circle
      v-for="dot in dots"
      :key="dot.key"
      class="version-history-graph__dot"
      :class="{ 'version-history-graph__dot--selected': dot.selected }"
      :cx="dot.x"
      :cy="dot.y"
      :r="4"
      :fill="dot.color"
      stroke="none"
    />
  </svg>
</template>

<script setup lang="ts">
/**
 * 版本历史分支图。
 *
 * 该组件只负责把服务层已经计算好的轻量图数据映射为 SVG，不持有交互状态，
 * 避免历史面板与图布局逻辑产生双向耦合。
 */
import { computed } from 'vue';
import type { VersionHistoryGraph } from '@/services/version-workspace/graph/build-version-history-graph';

interface GraphLine {
  /** SVG 元素 key。 */
  readonly key: string;
  /** 线段横坐标。 */
  readonly x: number;
  /** 线段起始纵坐标。 */
  readonly y1: number;
  /** 线段结束纵坐标。 */
  readonly y2: number;
  /** 线段颜色。 */
  readonly color: string;
}

interface GraphPath {
  /** SVG 元素 key。 */
  readonly key: string;
  /** SVG path 指令。 */
  readonly d: string;
  /** 路径颜色。 */
  readonly color: string;
}

interface GraphDot {
  /** SVG 元素 key。 */
  readonly key: string;
  /** 圆点横坐标。 */
  readonly x: number;
  /** 圆点纵坐标。 */
  readonly y: number;
  /** 圆点颜色。 */
  readonly color: string;
  /** 是否为当前选中提交。 */
  readonly selected: boolean;
}

const props = withDefaults(defineProps<{
  /** 由版本历史图构建器生成的轨道布局。 */
  graph: VersionHistoryGraph;
  /** 当前列表展示的提交 ID 顺序，用于让图形与列表行严格对齐。 */
  rowIds: readonly string[];
  /** 当前选中的提交 ID。 */
  selectedCommitId?: string | null;
  /** 单行高度。 */
  rowHeight?: number;
  /** 列表行之间的间距，必须与右侧列表 gap 保持一致。 */
  rowGap?: number;
  /** 圆点在单行内的纵向偏移，用于对齐提交标题而不是整行中心。 */
  rowContentOffsetY?: number;
  /** 分支轨道间距。 */
  laneGap?: number;
}>(), {
  selectedCommitId: null,
  rowHeight: 32,
  rowGap: 0,
  laneGap: 10,
});

const graphInset = 10;
const dotConnectionGap = 6;
const rowContentOffsetY = computed(() => props.rowContentOffsetY ?? props.rowHeight / 2);

/**
 * 将提交 ID 映射为图行数据，保证 SVG 绘制顺序与面板列表一致。
 *
 * @returns 提交 ID 到图行的查询表。
 */
const rowMap = computed(() => new Map(props.graph.rows.map((row) => [row.commitId, row])));

/**
 * 将提交 ID 映射为当前可见列表行下标。
 *
 * @returns 提交 ID 到列表行下标的查询表。
 */
const rowIndexMap = computed(() => new Map(props.rowIds.map((commitId, index) => [commitId, index])));

/**
 * 计算 SVG 宽度。
 *
 * @returns 可容纳所有分支轨道的 SVG 宽度。
 */
const svgWidth = computed(() => Math.max(82, graphInset * 2 + Math.max(1, props.graph.laneCount) * props.laneGap));

/**
 * 计算 SVG 高度。
 *
 * @returns 与提交行数对齐的 SVG 高度。
 */
const svgHeight = computed(() => {
  const rowCount = props.rowIds.length;
  if (rowCount === 0) return props.rowHeight;
  return Math.max(props.rowHeight, rowCount * props.rowHeight + Math.max(0, rowCount - 1) * props.rowGap);
});

/**
 * 读取列表实际行距。
 *
 * 右侧列表的 CSS gap 也会推开行的实际位置，图谱一起参与计算才能避免底部累计错位。
 *
 * @returns 单条提交在纵向上的完整位移。
 */
function getRowPitch(): number {
  return props.rowHeight + props.rowGap;
}

/**
 * 读取指定轨道的横坐标。
 *
 * @param laneIndex 分支轨道编号。
 * @returns 对应轨道在 SVG 中的横坐标。
 */
function getLaneX(laneIndex: number): number {
  return graphInset + laneIndex * props.laneGap;
}

/**
 * 读取指定提交行的圆点纵坐标。
 *
 * @param rowIndex 提交行下标。
 * @returns 与提交标题对齐的圆点纵坐标。
 */
function getRowY(rowIndex: number): number {
  return rowIndex * getRowPitch() + rowContentOffsetY.value;
}

/**
 * 计算同一轨道上相邻可见提交点之间的竖向连接段。
 *
 * @returns SVG line 元素数据。
 */
const verticalConnectionLines = computed<GraphLine[]>(() => props.rowIds.flatMap((commitId, rowIndex) => {
  const row = rowMap.value.get(commitId);
  if (!row) return [];

  const nextRowIndex = props.rowIds.findIndex((candidateCommitId, candidateIndex) => (
    candidateIndex > rowIndex
    && rowMap.value.get(candidateCommitId)?.laneIndex === row.laneIndex
  ));
  if (nextRowIndex === -1) return [];

  const x = getLaneX(row.laneIndex);
  const nextCommitId = props.rowIds[nextRowIndex];

  return [{
    key: `${commitId}-${nextCommitId}`,
    x,
    y1: getRowY(rowIndex) + dotConnectionGap,
    y2: getRowY(nextRowIndex) - dotConnectionGap,
    color: row.color,
  }];
}));

/**
 * 计算跨轨道父子关系的连接路径。
 *
 * @returns SVG path 元素数据。
 */
const branchPaths = computed<GraphPath[]>(() => props.rowIds.flatMap((commitId, rowIndex) => {
  const row = rowMap.value.get(commitId);
  if (!row) return [];

  const currentX = getLaneX(row.laneIndex);
  const currentY = getRowY(rowIndex);

  return row.parentConnections.flatMap((parentConnection) => {
    const parentRowIndex = rowIndexMap.value.get(parentConnection.commitId);
    if (parentRowIndex === undefined) return [];

    if (parentRowIndex <= rowIndex) return [];

    const parentX = getLaneX(parentConnection.laneIndex);
    const parentY = getRowY(parentRowIndex);
    // 用较长的缓弯接入子轨道，再沿子轨道上行，避免多条分支在底部挤成生硬折角。
    const entryDistance = Math.min(
      Math.abs(parentY - currentY) * 0.5,
      props.rowHeight * 1.2,
    );
    const branchStartY = parentY - dotConnectionGap;
    const entryY = branchStartY - entryDistance;
    const parentControlY = branchStartY - entryDistance * 0.2;
    const childControlY = branchStartY - entryDistance * 0.4;

    return [{
      key: `${commitId}-${parentConnection.commitId}`,
      d: `M ${parentX} ${branchStartY} C ${parentX} ${parentControlY}, ${currentX} ${childControlY}, ${currentX} ${entryY} L ${currentX} ${currentY + dotConnectionGap}`,
      color: row.color,
    }];
  });
}));

/**
 * 计算每个提交节点的圆点。
 *
 * @returns SVG circle 元素数据。
 */
const dots = computed<GraphDot[]>(() => props.rowIds.flatMap((commitId, rowIndex) => {
  const row = rowMap.value.get(commitId);
  if (!row) return [];

  return [{
    key: commitId,
    x: getLaneX(row.laneIndex),
    y: getRowY(rowIndex),
    color: row.color,
    selected: commitId === props.selectedCommitId,
  }];
}));
</script>

<style scoped>
.version-history-graph {
  display: block;
  overflow: visible;
  color: var(--n-border-color);
}

.version-history-graph__vertical-connection,
.version-history-graph__branch {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.version-history-graph__vertical-connection {
  shape-rendering: geometricPrecision;
}

.version-history-graph__dot {
  stroke: none;
}

.version-history-graph__dot--selected {
  filter: brightness(1.12);
}
</style>
